import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ReportsWorkloadColumn,
    ReportsWorkloadFilters,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { ReportColumnId, REPORT_COLUMN_IDS } from "org/reports/list/columns";

import { decodeOrFilter, encodeOrFilter } from "../common";

import { decodeAccessRights, encodeAccessRights } from "./common";

import { ReportsPageStateRaw } from ".";

type ReportsWorkloadPageStateWithType = PageStateData<"ReportsWorkload"> & {
    type: "ReportsWorkload";
};

function encodeReportsWorkloadPageState(
    raw: ReportsPageStateRaw
): ReportsWorkloadPageStateWithType {
    if (raw.pageType !== "ReportsWorkload") {
        throw new Error(
            `Expected page type to be "ReportsWorkload", but got "${raw.pageType}"`
        );
    }

    return {
        type: "ReportsWorkload",
        version: raw.version,
        columns: Array.from(raw.visibleColumnIds).map(
            encodeReportsWorkloadColumn
        ),
        filters: encodeReportsWorkloadFilters(raw.filter),
        leftSideMenuCollapsed: raw.leftSideMenuCollapsed,
    };
}

function decodeReportsWorkloadPageState(
    input: ReportsWorkloadPageStateWithType
): ReportsPageStateRaw {
    if (input.type !== "ReportsWorkload") {
        throw new Error(
            `Expected page type to be "ReportsWorkload", but got "${input.type}"`
        );
    }

    const visibleColumnIdsAsArray = input.columns.map(
        decodeReportsWorkloadColumn
    );

    return {
        pageType: "ReportsWorkload",
        version: input.version,
        visibleColumnIds: new Set(visibleColumnIdsAsArray),
        filter: decodeReportsWorkloadFilters(input.filters),
        leftSideMenuCollapsed: input.leftSideMenuCollapsed,
    };
}

function encodeReportsWorkloadColumn(
    rawColumn: ReportColumnId
): ReportsWorkloadColumn {
    switch (rawColumn) {
        case REPORT_COLUMN_IDS.NAME:
            return "name";
        case REPORT_COLUMN_IDS.DESCRIPTION:
            return "description";
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
            return "pushReportsCount";
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
            return "accessRights";
        case REPORT_COLUMN_IDS.ACTION:
            return "actions";
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
        case REPORT_COLUMN_IDS.DATASHEET:
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
        case REPORT_COLUMN_IDS.CLASSIFICATION:
        case REPORT_COLUMN_IDS.REPORT_TYPE:
            throw new Error(
                `Unknown column for ReportsWorkload page: ${rawColumn}`
            );
        default:
            return assertNever(rawColumn);
    }
}

function decodeReportsWorkloadColumn(
    column: ReportsWorkloadColumn
): ReportColumnId {
    switch (column) {
        case "name":
            return REPORT_COLUMN_IDS.NAME;
        case "description":
            return REPORT_COLUMN_IDS.DESCRIPTION;
        case "pushReportsCount":
            return REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT;
        case "accessRights":
            return REPORT_COLUMN_IDS.ACCESS_RIGHTS;
        case "actions":
            return REPORT_COLUMN_IDS.ACTION;
        default:
            return assertNever(column);
    }
}

function encodeReportsWorkloadFilters(
    filter: SimpleAndFilter | null
): ReportsWorkloadFilters {
    if (filter === null) {
        return {};
    }

    const filters = filter.filters;
    return {
        name: encodeOrFilter(REPORT_COLUMN_IDS.NAME, filters),
        description: encodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters),
        pushReportsCount: encodeOrFilter(
            REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
            filters
        ),
        accessRights: encodeAccessRights(filters),
    };
}

function decodeReportsWorkloadFilters(
    filters: ReportsWorkloadFilters
): SimpleAndFilter | null {
    if (Object.keys(filters).length === 0) {
        return null;
    }

    const orFilters: Array<SimpleOrFilter> = [];
    if (filters.name) {
        orFilters.push(decodeOrFilter(REPORT_COLUMN_IDS.NAME, filters.name));
    }
    if (filters.description) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters.description)
        );
    }
    if (filters.pushReportsCount) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
                filters.pushReportsCount
            )
        );
    }
    if (filters.accessRights) {
        const accessRightFilters = decodeAccessRights(filters.accessRights);
        orFilters.push(...accessRightFilters);
    }

    return {
        filters: orFilters,
    };
}

export { encodeReportsWorkloadPageState, decodeReportsWorkloadPageState };

export type { ReportsWorkloadPageStateWithType };
